import type { TaskSubmitFormValues } from 'src/components/GenerationTask/BaseTaskSubmitForm'
import type { LoraTriggerWordsBehaviour } from 'src/components/GenerationTask/MultiColumnGenerationForm/Generation/LoraTriggerWords/LoraTriggerWordsContext'
import type { ArtworkBaseFragment } from '../apollo/generated/graphql'
import {
  cleanupLoraParameters,
  mergePromptsWithTriggerWords,
  stripLoraParametersTriggerWords,
} from './loraParameters'

export const getArtworkParameters = (
  artwork?: Pick<ArtworkBaseFragment, 'extra' | 'prompts'> & {
    hidePrompts?: boolean
  },
  task?: {
    parameters?: Record<string, any>
    outputs?: Record<string, any>
  },
  options?: {
    loraTriggerWordsBehaviour?: LoraTriggerWordsBehaviour
  },
) => {
  const { loraTriggerWordsBehaviour } = options ?? {}

  const p = {
    ...artwork?.extra?.externalParameters,
    ...task?.parameters,
  }

  const naturalPrompts = getNaturalPrompts(artwork, task)
  /** Prompts will be empty if artwork's prompts are set to hidden */
  const rawPrompts =
    artwork?.prompts ??
    (artwork?.hidePrompts ? undefined : task?.parameters?.prompts)
  const prompts = typeof rawPrompts === 'string' ? rawPrompts : undefined
  const loraParameters = cleanupLoraParameters(
    p.loraParameters,
    p.lora,
    artwork?.hidePrompts,
  )
  const enableNaturalPrompts = naturalPrompts && !artwork?.hidePrompts
  const userInputPrompts = enableNaturalPrompts ? naturalPrompts : prompts

  return {
    ...(loraTriggerWordsBehaviour === 'rich-text-prompt'
      ? {
          // let rich prompts editor parse the data structure
          prompts: userInputPrompts,
          loraParameters,
        }
      : {
          prompts: mergePromptsWithTriggerWords(
            userInputPrompts,
            loraParameters,
          ),
          loraParameters: stripLoraParametersTriggerWords(loraParameters),
        }),
    enablePromptsRewrite: enableNaturalPrompts == true, // Explicitly disable promptsRewrite when not using natural prompts
    negativePrompts: p.negativePrompts || p.negative_prompt,
    samplingSteps: p.samplingSteps || p.steps,
    samplingMethod: p.samplingMethod || p.sample,
    cfgScale: p.cfgScale || p.cfg_scale,
    seed: task?.parameters?.seed, // Should read from input only
    modelId: task?.parameters?.modelId, // Should read from input only

    // controlnet
    // outputs.parameters.controlNets are different from task.parameters.controlNets
    // don't use output parameters
    controlNets: task?.parameters?.controlNets ?? [],

    // lora
    lora: p.lora,

    // hires
    upscale: p.upscale,
    upscaleDenoisingSteps: p.upscaleDenoisingSteps,
    upscaleDenoisingStrength: p.upscaleDenoisingStrength,

    batchSize:
      p.batchSize == null
        ? 1 // batchSize should be 1 if not provided
        : p.batchSize === 3
          ? 4 // treat 3 as 4
          : p.batchSize,

    // a detailer
    enableADetailer: p.enableADetailer,

    // clipSkip
    clipSkip: p.clipSkip,

    // vaeModel
    vaeModelId: p.vaeModelId,
    ipAdapter: p.ipAdapter
      ? // Submitted media is always valid
        { ...p.ipAdapter, imagesAreValid: true }
      : undefined,
    qualityTag: p.qualityTag ? p.qualityTag : undefined,

    // character
    characterId: p.characterId,
    styleMix: p.styleMix?.enabled ? p.styleMix : undefined,
    lightning: p.lightning,

    modelStyle: p.modelStyle,
    watermark: p.watermark,

    inferenceProfile: p.inferenceProfile,
    inferenceConfig: p.inferenceConfig,
  } satisfies TaskSubmitFormValues
}

export function getNaturalPrompts(
  artwork?: Pick<ArtworkBaseFragment, 'extra'>,
  task?: {
    parameters?: Record<string, any>
  },
) {
  return (
    (artwork?.extra?.naturalPrompts as string | undefined) ||
    (task?.parameters?.extra?.naturalPrompts as string | undefined)
  )
}
